<template>
  <div class="forward-address-details">
    <label class="contact-details__inline-input">
      Title
      <SoSelect
        v-model="forwardAddress.title"
        :items="titleOptions"
        @update:model-value="updateField('title', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Care of
      <SoTextField
        :model-value="forwardAddress.careOf"
        :rules="[rules.required]"
        type="text"
        @update:model-value="updateField('careOf', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Address 1
      <SoTextField
        :model-value="forwardAddress.addressLine1"
        :rules="[rules.required]"
        type="text"
        @update:model-value="updateField('addressLine1', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Address 2
      <SoTextField
        :model-value="forwardAddress.addressLine2"
        type="text"
        @update:model-value="updateField('addressLine2', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Country code
      <SoTextField
        :model-value="forwardAddress.country"
        :rules="[rules.required]"
        type="text"
        @update:model-value="updateField('country', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Postcode
      <SoTextField
        :model-value="forwardAddress.postcode"
        :rules="[rules.required, rules.postcode]"
        type="text"
        @update:model-value="updateField('postcode', $event)"
      />
    </label>
  </div>
  <SoButton
    class="mt-2"
    title="Customer is unable to give forwarding address"
    type="text"
    @click="isForwardAddressModalShown = true"
  />
  <CotForwardAddressModal
    v-model="isForwardAddressModalShown"
    @on-supply-address="populateForwardAddressForm"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { cloneDeep } from "lodash"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import rules from "@/validators/rules"
  import { IAddress, IForwardAddress } from "@/models/address/address.types"
  import SoButton from "@/components/formControls/SoButton.vue"
  import CotForwardAddressModal from "@/views/customer/changeOfTenancy/components/CotForwardAddressModal.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { TITLE_OPTIONS } from "@/constants/titleOptions"
  import { useCurrentAccountStore } from "@/stores/currentAccount"

  const emptyForwardAddress: IForwardAddress = {
    title: "",
    careOf: "",
    city: "",
    addressLine1: "",
    addressLine2: "",
    postcode: "",
    country: "",
    reason: "",
  }

  export default defineComponent({
    components: {
      SoSelect,
      CotForwardAddressModal,
      SoButton,
      SoTextField,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: Object as PropType<IForwardAddress>,
        default: () => ({}),
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        titleOptions: TITLE_OPTIONS,
        isForwardAddressModalShown: false,
        forwardAddress: emptyForwardAddress,
      }
    },
    computed: {
      rules() {
        return rules
      },
      supplyAddress(): IAddress | undefined {
        return this.current.account.data.supplyAddress
      },
      name(): string {
        return this.current.account.data.name
      },
    },
    created() {
      if (this.modelValue?.addressLine1 || this.modelValue?.postcode) {
        this.forwardAddress = {
          ...emptyForwardAddress,
          ...cloneDeep(this.modelValue),
        }
      }
    },
    methods: {
      updateField(fieldName: string, fieldValue: string) {
        this.forwardAddress = {
          ...this.forwardAddress,
          [fieldName]: fieldValue,
        }
        this.$emit("update:modelValue", this.forwardAddress)
      },
      populateForwardAddressForm(supplyAddress: {
        hasSupplyAddress: boolean
        reason?: string
      }) {
        if (supplyAddress.hasSupplyAddress) {
          this.forwardAddress = {
            ...this.forwardAddress,
            careOf: this.name,
            country: this.supplyAddress?.countryCode ?? "",
            addressLine1: this.supplyAddress?.addressLine1 ?? "",
            addressLine2: this.supplyAddress?.addressLine2 ?? "",
            postcode: this.supplyAddress?.postcode ?? "",
            reason: supplyAddress.reason ?? "",
          }

          return this.$emit("update:modelValue", this.forwardAddress)
        }

        this.forwardAddress = emptyForwardAddress
      },
    },
  })
</script>

<style scoped>
  .forward-address-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
</style>
